import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Box, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { MY_HR_GET_COMPANIES } from "my-hr/graphql";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
} from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import {
  useOcProperty,
  useOcSnackbar,
  useOcClientState,
  useOcIsMobile,
} from "oc/hooks";

export const MyHrSystemSettingsCompanySelectorCard = () => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  const { setOpenMessage } = useOcSnackbar();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch selected company property
  const { data: selectedCompanyCode } = useOcProperty({
    resource: "system",
    key: "selectedCompany",
  });

  // Temporary state for selected company
  const [selectedCompanyTempState, setSelectedCompanyTempState] =
    useOcClientState("selectedCompanyTempState", selectedCompanyCode || "001");

  // Update temp state when property loads
  useEffect(() => {
    if (selectedCompanyCode && selectedCompanyTempState !== selectedCompanyCode) {
      setSelectedCompanyTempState(selectedCompanyCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyCode]);

  // Fetch companies list
  const { data: companiesData, loading: companiesLoading } = useQuery(
    MY_HR_GET_COMPANIES,
    {
      variables: {
        searchField: "code",
        searchText: "",
        sort: "code",
        page: 1,
        pageSize: 1000,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const companies = companiesData?.companies?.rows || [];

  // Update property mutation
  const [updateProperty, { loading: updateLoading }] = useMutation(
    OC_UPDATE_PROPERTY,
    {
      onCompleted: () => {
        setOpenMessage({
          type: "success",
          message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
        });
        setShowConfirmDialog(false);
      },
      onError({ ...err }) {
        alert("error" + err.toString());
        setOpenMessage({
          type: "error",
          message: t("OC_PROPERTY_SAVE_FAILED"),
        });
        setShowConfirmDialog(false);
      },
    }
  );

  const handleSave = () => {
    updateProperty({
      variables: {
        resource: "system",
        key: "selectedCompany",
        value: selectedCompanyTempState,
      },
    });
  };

  const handleCancel = () => {
    setSelectedCompanyTempState(selectedCompanyCode || "001");
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const saveDisabled =
    selectedCompanyTempState === selectedCompanyCode ||
    !selectedCompanyTempState ||
    updateLoading;

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_HR_SELECTED_COMPANY")}>
        <Box width={isMobile ? "auto" : "585px"} padding="24px 0px">
          <FormControl
            fullWidth
            variant="outlined"
            disabled={companiesLoading}
          >
            <InputLabel id="company-selector-label">
              {t("MY_HR_SELECT_COMPANY")}
            </InputLabel>
            <Select
              labelId="company-selector-label"
              id="company-selector"
              value={selectedCompanyTempState || "001"}
              onChange={(e) => setSelectedCompanyTempState(e.target.value)}
              label={t("MY_HR_SELECT_COMPANY")}
            >
              {companies.map((company: any) => (
                <MenuItem key={company.code} value={company.code}>
                  {company.code} - {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonColor="secondary"
          rightButtonText={t("OC_CANCEL")}
          rightButtonId="cancel-company-selector"
          handlePrimaryButton={() => setShowConfirmDialog(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={saveDisabled}
          primaryButtonId="save-company-selector"
        />
      </OcCard>
    </>
  );
};

export default MyHrSystemSettingsCompanySelectorCard;
